/**
 * MED-817 / E4.5 — Peer Leader post-session "Medley Feedback Form" question set.
 *
 * AC9: the peer leader fills out TWO sections reflecting their dual role —
 * "As Session Leader" (shown first) and "As a Participant" (shown second).
 * Fields are a mix of labeled 5-point scales, single-selects, and open text.
 *
 * Treated as data (not hardcoded JSX) so the renderer
 * (`PeerLeaderFeedbackFormCard`) stays generic. Answers persist as JSON string
 * values in `peer_leader_session_feedback.responses` (JSONB), keyed by
 * `PeerLeaderFeedbackQuestion.key`. Scale answers store the numeric value as a
 * string ("4"); single-selects store the option `value` slug; open text stores
 * the raw string. All manual entry — AI pre-population is deferred to E4.6.
 */

export type PeerLeaderFeedbackFieldType = 'scale' | 'singleSelect' | 'textarea';

export interface PeerLeaderFeedbackScaleOption {
  value: number;
  label: string;
}

export interface PeerLeaderFeedbackSelectOption {
  value: string;
  label: string;
}

export interface PeerLeaderFeedbackQuestion {
  key: string;
  questionText: string;
  type: PeerLeaderFeedbackFieldType;
  required: boolean;
  /** For `scale` — the 5 labeled points, low → high. */
  scaleOptions?: PeerLeaderFeedbackScaleOption[];
  /** For `singleSelect` — the choices in display order. */
  selectOptions?: PeerLeaderFeedbackSelectOption[];
  placeholder?: string;
}

export interface PeerLeaderFeedbackSection {
  id: 'as_leader' | 'as_participant';
  title: string;
  subtitle: string;
  questions: PeerLeaderFeedbackQuestion[];
}

/** 5-point agreement scale (Strongly disagree → Strongly agree). */
const AGREEMENT_SCALE: PeerLeaderFeedbackScaleOption[] = [
  { value: 1, label: 'Strongly disagree' },
  { value: 2, label: 'Disagree' },
  { value: 3, label: 'Neutral' },
  { value: 4, label: 'Agree' },
  { value: 5, label: 'Strongly agree' },
];

/** 5-point satisfaction scale (Very dissatisfied → Very satisfied). */
const SATISFACTION_SCALE: PeerLeaderFeedbackScaleOption[] = [
  { value: 1, label: 'Very dissatisfied' },
  { value: 2, label: 'Dissatisfied' },
  { value: 3, label: 'Neither satisfied nor dissatisfied' },
  { value: 4, label: 'Satisfied' },
  { value: 5, label: 'Very satisfied' },
];

const AS_LEADER_QUESTIONS: PeerLeaderFeedbackQuestion[] = [
  {
    key: 'leader_materials_sufficient',
    questionText: 'The session materials were sufficient to guide me as the session leader.',
    type: 'scale',
    required: true,
    scaleOptions: AGREEMENT_SCALE,
  },
  {
    key: 'leader_prep_time',
    questionText: 'How much time did you spend preparing to lead the session?',
    type: 'singleSelect',
    required: true,
    selectOptions: [
      { value: '0_15', label: '0–15 minutes' },
      { value: '15_30', label: '15–30 minutes' },
      { value: '30_plus', label: '30+ minutes' },
      { value: 'other', label: 'Other' },
    ],
  },
  {
    key: 'leader_comfort',
    questionText: 'How comfortable did you feel leading the session?',
    type: 'singleSelect',
    required: true,
    selectOptions: [
      { value: 'very_comfortable', label: 'Very Comfortable' },
      { value: 'comfortable', label: 'Comfortable' },
      { value: 'uncomfortable', label: 'Uncomfortable' },
      { value: 'very_uncomfortable', label: 'Very Uncomfortable' },
    ],
  },
  {
    key: 'leader_followed_agenda',
    questionText: 'How closely did you follow the agenda?',
    type: 'singleSelect',
    required: true,
    selectOptions: [
      { value: 'very_closely', label: 'Very closely' },
      { value: 'loosely', label: 'Loosely' },
      { value: 'not_at_all', label: 'Not at all' },
    ],
  },
  {
    key: 'leader_advice_next',
    questionText: 'What advice do you have for the next session leader?',
    type: 'textarea',
    required: true,
  },
  {
    key: 'leader_what_worked',
    questionText: 'What worked for you about this session?',
    type: 'textarea',
    required: false,
  },
  {
    key: 'leader_what_improve',
    questionText: 'What could be improved?',
    type: 'textarea',
    required: false,
  },
  {
    key: 'leader_anything_else',
    questionText: "Anything else you'd like to share?",
    type: 'textarea',
    required: false,
  },
];

const AS_PARTICIPANT_QUESTIONS: PeerLeaderFeedbackQuestion[] = [
  {
    key: 'participant_present_intentional',
    questionText: 'I was present and intentional in this session.',
    type: 'scale',
    required: true,
    scaleOptions: AGREEMENT_SCALE,
  },
  {
    key: 'participant_group_understood',
    questionText: 'The group understood my challenges.',
    type: 'scale',
    required: true,
    scaleOptions: AGREEMENT_SCALE,
  },
  {
    key: 'participant_could_be_open',
    questionText: 'I felt that I could be open in the group.',
    type: 'scale',
    required: true,
    scaleOptions: AGREEMENT_SCALE,
  },
  {
    key: 'participant_thoughts_expanded',
    questionText:
      'My thoughts and views have been expanded as a result of the discussion.',
    type: 'scale',
    required: true,
    scaleOptions: AGREEMENT_SCALE,
  },
  {
    key: 'participant_action_step',
    questionText:
      'I came away from the group with an action step to help me move forward on something I care about.',
    type: 'scale',
    required: true,
    scaleOptions: AGREEMENT_SCALE,
  },
  {
    key: 'participant_satisfaction',
    questionText: 'How satisfied are you with your overall Medley experience?',
    type: 'scale',
    required: true,
    scaleOptions: SATISFACTION_SCALE,
  },
  {
    key: 'participant_enjoyed',
    questionText: 'What did you enjoy about the session?',
    type: 'textarea',
    required: false,
  },
  {
    key: 'participant_improvements',
    questionText: 'What improvements can we make for future sessions?',
    type: 'textarea',
    required: false,
  },
];

export const peerLeaderFeedbackSections: PeerLeaderFeedbackSection[] = [
  {
    id: 'as_leader',
    title: 'As Session Leader',
    subtitle: 'Reflect on how it went leading this session.',
    questions: AS_LEADER_QUESTIONS,
  },
  {
    id: 'as_participant',
    title: 'As a Participant',
    subtitle: 'Reflect on your own experience in the group.',
    questions: AS_PARTICIPANT_QUESTIONS,
  },
];

/**
 * Reserved meta key carried inside the `responses` JSONB blob to mark the form
 * as submitted (AC10/AC12). Lives in the blob because
 * `peer_leader_session_feedback.submitted_at` is NOT NULL (always stamped on
 * save), so it can't itself distinguish a saved draft from a real submission
 * without a migration. This key is double-underscored to never collide with a
 * question key, and the renderer/validation iterate the question config — not
 * the responses map — so it is never shown or validated as a question.
 */
export const FEEDBACK_SUBMITTED_FLAG = '__submitted';

/** True when the responses blob has been marked submitted (vs. a saved draft). */
export function isFeedbackSubmitted(responses: Record<string, string>): boolean {
  return responses[FEEDBACK_SUBMITTED_FLAG] === 'true';
}

/** Flat list of every question across both sections. */
export const peerLeaderFeedbackQuestions: PeerLeaderFeedbackQuestion[] =
  peerLeaderFeedbackSections.flatMap((s) => s.questions);

/** Keys of all required questions — used for Submit validation (AC9 asterisks). */
export const peerLeaderRequiredFeedbackKeys: string[] = peerLeaderFeedbackQuestions
  .filter((q) => q.required)
  .map((q) => q.key);

/**
 * Returns the list of required question keys that are still blank in the given
 * responses map. Empty array → the form is complete enough to submit.
 */
export function getMissingRequiredFeedbackKeys(
  responses: Record<string, string>,
): string[] {
  return peerLeaderRequiredFeedbackKeys.filter((key) => {
    const v = responses[key];
    return v === undefined || v === null || v.trim() === '';
  });
}
